import { type FC, useEffect, useState } from "react";
import { FiInfo, FiMail, FiPhone } from "react-icons/fi";
import { Link } from "react-router";
import { getActiveHolidayNotice, type HolidayNotice } from "../../../constants/holidayNotice";
import { Container } from "../components/Container";
import { HolidayNoticeBanner } from "../components/HolidayNoticeBanner";
import { MailDraftComposer } from "../components/MailDraftComposer";
import { Notice } from "../components/Notice";
import { PageHeading } from "../components/PageHeading";
import { SectionHeading } from "../components/SectionHeading";
import { buildContactMail } from "./contactMailto";

/** フリーダイヤル施策のオプトイン時に切り替わるため、表示中の番号はサーバから取得する。 */
type SupportPhone = { display: string; tel: string };
const DEFAULT_PHONE: SupportPhone = { display: "03-4565-4839", tel: "tel:0345654839" };

const useSupportPhone = (): SupportPhone => {
  const [phone, setPhone] = useState<SupportPhone>(DEFAULT_PHONE);
  useEffect(() => {
    const controller = new AbortController();
    fetch("/mypage/api/support-phone", { signal: controller.signal })
      .then((res) => (res.ok ? (res.json() as Promise<SupportPhone>) : Promise.reject()))
      .then((data) => {
        if (data?.display && data?.tel) setPhone(data);
      })
      // 取得失敗・アンマウントによる中断時は通常番号 (デフォルト) のままにする。
      .catch(() => {});
    return () => controller.abort();
  }, []);
  return phone;
};

/**
 * お問い合わせ先ページ。
 * `c-support@survaq.com` をフッタに直接書く運用から、本ページにリンクを集約する形に変更した。
 *
 * 内容は help.survaq-store.com の「お問い合わせ先について」 ページに合わせる。
 * 文言の単一情報源 (SSOT) はあちらなので、本ページも合わせて更新すること。
 *
 * ルーティング: `PublicLayout` 配下に置く (= 未ログイン状態でも見れる)。
 * ログインページのフッタからも参照されるため。
 */
type Props = {
  /**
   * 表示する長期休暇の告知。既定では現在日 (JST) から判定する。Storybook / テストで
   * 「告知あり / なし」を現在日に左右されず固定できるよう props で差し込めるようにしている。
   */
  holidayNotice?: HolidayNotice | null;
};

export const ContactPage: FC<Props> = ({ holidayNotice = getActiveHolidayNotice("orders") }) => {
  const phone = useSupportPhone();
  return (
    <Container>
      <PageHeading
        title="お問い合わせ先"
        description="ご注文に関するお問い合わせは、下記のメール または お電話にて承っております。"
      />

      {/* 配送に関する問い合わせが集まる入口なので告知する */}
      <HolidayNoticeBanner notice={holidayNotice} />

      <Notice variant="info" className="mb-8">
        Amazon・楽天市場・Yahoo! ショッピングでご購入の方は、各モールのお問い合わせ窓口より
        ご連絡ください。{" "}
        <Link
          to="/external-mall-contact"
          className="underline underline-offset-4 hover:text-neutral-900"
        >
          モール別のお問い合わせ先はこちら
        </Link>
      </Notice>

      <section className="mb-10">
        <SectionHeading
          title={
            <span className="inline-flex items-center gap-2">
              <FiMail size={18} className="text-neutral-500" />
              メールでのお問い合わせ
            </span>
          }
        />
        <div className="space-y-5 py-4 text-sm">
          <Notice variant="highlight" icon={<FiInfo size={16} />}>
            <p className="mb-1 font-medium">
              ご注文に関するお問い合わせの場合は、本文の【ご注文情報】を必ずご記入ください。
            </p>
            <p>
              注文番号やご本人を確認する情報がないとご注文をお調べできず、ご返答までにお時間をいただきます。ご注文前のお問い合わせでは、ご記入は不要です。
            </p>
          </Notice>

          <MailDraftComposer draft={buildContactMail()} buttonLabel="メールを作成する" />

          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-4 text-neutral-600 text-xs leading-relaxed">
            <p>営業時間内 (10:00 〜 18:00) にて順次ご対応いたします。</p>
            <p>
              原則 3 営業日以内にご返信いたします。お急ぎの場合や、
              メールでのご連絡が難しい場合はお電話にてお問い合わせください。
            </p>
          </div>
        </div>
      </section>

      <section className="mb-10">
        <SectionHeading
          title={
            <span className="inline-flex items-center gap-2">
              <FiPhone size={18} className="text-neutral-500" />
              お電話でのお問い合わせ
            </span>
          }
        />
        <div className="space-y-4 py-2 text-sm">
          <p>
            <a
              href={phone.tel}
              className="font-medium text-neutral-900 underline underline-offset-4 hover:text-neutral-700"
            >
              {phone.display}
            </a>
          </p>
          <p className="text-neutral-700">受付時間: 10:00 〜 18:00 (月 〜 金、土日祝を除く)</p>
          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-4 text-neutral-600 text-xs leading-relaxed">
            <p>
              10:00 〜 12:00 の時間帯は特にお電話が繋がりにくくなっております。
              繋がらない場合は、お時間を改めてお掛け直しいただくか、メールにてお問い合わせください。
            </p>
          </div>
        </div>
      </section>

      <footer className="mt-12 border-neutral-200 border-t pt-6 text-center text-neutral-500 text-xs">
        <Link to="/" className="underline underline-offset-4 hover:text-neutral-900">
          マイページに戻る
        </Link>
      </footer>
    </Container>
  );
};
